// src/components/SubcategoryResourceManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from "../../../firebaseConfig";
import { 
  fetchResources, uploadResourceFile, addResourceLink, deleteResource, 
  updateProgress, fetchStudentProgress, renameFolder, createNewFolder, deleteFolderMeta
} from "../../../utils/tutorUtils";

const styles = {
  page: {
    padding: '32px 24px 120px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    backgroundColor: '#f3f4f6',
  },

  // HEADER
  header: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  title: {
    fontSize: '26px',
    fontWeight: 800,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
  },
  metaRow: {
    marginTop: '4px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    fontSize: '12px',
  },
  pill: {
    padding: '4px 10px',
    borderRadius: '999px',
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
    fontWeight: 500,
  },
  backBtn: {
    background: '#ffffff',
    border: '1px solid #d1d5db',
    padding: '8px 16px',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 600,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },

  // TOP TOOLBAR
  toolbarCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '16px 20px',
    marginBottom: '24px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toolbarLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  toolbarTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  toolbarHint: {
    fontSize: '12px',
    color: '#6b7280',
  },
  topBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
  },
  createFolderInput: {
    padding: '8px 10px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    width: '220px',
    fontSize: '13px',
    backgroundColor: '#f9fafb',
  },
  createFolderBtn: {
    padding: '8px 14px',
    background: '#111827',
    color: '#ffffff',
    borderRadius: '999px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '13px',
  },

  // MAIN CONTENT WRAPPER
  content: {
    background: '#ffffff',
    borderRadius: '18px',
    boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
    padding: '20px 20px 24px',
    minHeight: '60vh',
    border: '1px solid #e5e7eb',
  },
  loadingBox: {
    padding: '40px 0',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '14px',
  },

  // FOLDER SECTION
  folderSection: {
    marginBottom: '24px',
    borderRadius: '14px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    backgroundColor: '#f9fafb',
  },
  folderHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: '#f3f4ff',
    borderBottom: '1px solid #e5e7eb',
  },
  folderTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  folderTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  folderBadge: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '999px',
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 600,
  },
  folderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  folderActionBtn: {
    fontSize: '11px',
    color: '#374151',
    background: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '999px',
    cursor: 'pointer',
    padding: '4px 10px',
  },
  deleteFolderBtn: {
    fontSize: '11px',
    color: '#b91c1c',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '999px',
    cursor: 'pointer',
    padding: '4px 10px',
  },
  openUploadBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    background: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 600,
  },

  // INLINE UPLOAD FORM
  inlineForm: {
    padding: '12px 14px',
    background: '#eff6ff',
    borderBottom: '1px solid #dbeafe',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  formTypeRow: {
    display: 'flex',
    gap: '12px',
    fontSize: '13px',
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
  },
  inputText: {
    padding: '7px 10px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    backgroundColor: '#ffffff',
  },
  inlineSaveBtn: {
    padding: '7px 14px',
    borderRadius: '999px',
    border: 'none',
    backgroundColor: '#111827',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  inlineCancel: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    textDecoration: 'underline',
    color: '#6b7280',
  },

  // RESOURCE CARD
  resourceRow: {
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
  },
  resourceCard: {
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  resourceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  resourceInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
    cursor: 'pointer',
  },
  icon: {
    fontSize: '20px',
  },
  linkText: {
    fontSize: '14px',
    color: '#2563eb',
    fontWeight: 600,
    textDecoration: 'underline',
  },
  statusGroup: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  statusPill: (active, color, subtleBg) => ({
    padding: '4px 9px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${active ? color : '#d1d5db'}`,
    color: active ? color : '#4b5563',
    backgroundColor: active ? subtleBg : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  }),

  resourceBody: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 180px) minmax(0, 1.7fr) minmax(0, 140px)',
    gap: '12px',
  },

  // SCORE / PROGRESS
  scoreContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '12px',
    color: '#4b5563',
  },
  scoreInputsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  scoreInput: {
    width: '38px',
    padding: '4px 6px',
    textAlign: 'center',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    backgroundColor: '#f9fafb',
  },
  battery: () => ({
    width: '100%',
    height: '10px',
    background: '#e5e7eb',
    borderRadius: '999px',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid #d1d5db',
  }),
  batteryFill: (pct) => {
    let color = '#ef4444';
    if (pct >= 60) color = '#eab308';
    if (pct >= 80) color = '#22c55e';
    return {
      width: `${pct}%`,
      height: '100%',
      background: color,
      transition: 'width 0.25s ease',
    };
  },

  // NOTES
  noteContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  noteLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#4b5563',
  },
  noteArea: {
    width: '100%',
    minHeight: '52px',
    padding: '8px 9px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '12px',
    fontFamily: 'inherit',
    resize: 'vertical',
    backgroundColor: '#f9fafb',
  },
  saveNoteBtn: {
    alignSelf: 'flex-end',
    fontSize: '11px',
    padding: '4px 10px',
    background: '#111827',
    color: '#ffffff',
    borderRadius: '999px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
  },

  // ACTIONS COLUMN
  actionColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-end',
  },
  addKeyBtn: {
    fontSize: '11px',
    background: '#eff6ff',
    color: '#2563eb',
    border: '1px solid #bfdbfe',
    borderRadius: '999px',
    padding: '5px 10px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  deleteBtn: {
    color: '#b91c1c',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '999px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  // CHILD (ANSWER KEY) ROWS
  rowChild: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 18px 8px 34px',
    background: '#f9fafb',
    borderTop: '1px solid #f3f4f6',
    fontSize: '13px',
  },
  childLabel: {
    fontWeight: 700,
    color: '#9ca3af',
    marginRight: '6px',
    fontSize: '10px',
    textTransform: 'uppercase',
  },
  childInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  childName: {
    fontSize: '13px',
    color: '#2563eb',
    fontWeight: 500,
    textDecoration: 'underline',
  },

  emptyFolder: {
    padding: '12px 16px',
    color: '#9ca3af',
    fontStyle: 'italic',
    fontSize: '13px',
    backgroundColor: '#ffffff',
  },

  // RESPONSIVE
  '@media(max-width: 900px)': {}, // (kept for possible future extension)
};

const getPct = (c, t) => {
  const correct = parseFloat(c) || 0;
  const total = parseFloat(t) || 0;
  return total > 0 ? Math.min(100, Math.max(0, (correct / total) * 100)) : 0;
};

const getFolderAccent = (folder) => {
  const name = (folder || '').toLowerCase();
  if (name === 'easy') return '#22c55e';
  if (name === 'medium') return '#eab308';
  if (name === 'hard') return '#ef4444';
  return '#6366f1';
};

export default function SubcategoryResourceManager() {
  const { studentId, subject, unit, subcategory } = useParams();
  const navigate = useNavigate();
  
  const [resources, setResources] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Local UI States
  const [newFolderName, setNewFolderName] = useState('');
  const [activeUploadFolder, setActiveUploadFolder] = useState(null);
  const [activeKeyParent, setActiveKeyParent] = useState(null);

  // Upload Form State
  const [addMode, setAddMode] = useState('file'); 
  const [fileObj, setFileObj] = useState(null);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  // Note State
  const [noteText, setNoteText] = useState({}); // { resourceId: "text..." }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchResources(subject, unit, subcategory);
      setResources(list);
      const pMap = await fetchStudentProgress(studentId);
      setProgressMap(pMap);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  }, [subject, unit, subcategory, studentId]);

  useEffect(() => { loadData(); }, [loadData]);

  // --- FOLDER ACTIONS ---
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    const exists = resources.some(r => r.folder === name);
    if (exists) { 
      alert("Folder already exists!"); 
      return; 
    }
    await createNewFolder(subject, unit, subcategory, name);
    setNewFolderName('');
    await loadData();
  };

  const handleRenameFolder = async (oldName) => {
    const newName = prompt("Enter new folder name:", oldName);
    if (newName && newName !== oldName) {
      await renameFolder(subject, unit, subcategory, oldName, newName);
      await loadData();
    }
  };

  const handleDeleteFolder = async (folderName) => {
    const hasFiles = resources.some(r => r.folder === folderName && r.type !== 'folder_meta');
    if (hasFiles) {
      alert("Please delete all files in this folder first.");
      return;
    }
    if (window.confirm(`Delete folder "${folderName}"?`)) {
      await deleteFolderMeta(subject, unit, subcategory, folderName);
      await loadData();
    }
  };

  // --- UPLOAD ACTIONS ---
  const handleUpload = async (folder, parentId = null) => {
    if (!studentId) return;
    setUploading(true);
    try {
      if (addMode === 'file' && fileObj) {
        await uploadResourceFile(fileObj, subject, unit, subcategory, folder, parentId);
      } else if (addMode === 'link' && linkName && linkUrl) {
        await addResourceLink(linkName, linkUrl, subject, unit, subcategory, folder, parentId);
      }
      setFileObj(null); 
      setLinkName(''); 
      setLinkUrl(''); 
      setActiveUploadFolder(null); 
      setActiveKeyParent(null);
      await loadData();
    } catch (err) { 
      alert("Error: " + err.message); 
    } finally { 
      setUploading(false); 
    }
  };

  const handleDeleteResource = async (r) => {
    if (window.confirm(`Delete "${r.name}"?`)) {
      await deleteResource(r.id, r);
      await loadData();
    }
  };

  // --- PROGRESS & NOTES ---
  const updateField = async (resourceId, field, value) => {
    setProgressMap(prev => ({ 
      ...prev, 
      [resourceId]: { ...prev[resourceId], [field]: value } 
    }));
    await updateProgress(studentId, resourceId, { [field]: value }, auth.currentUser?.email);
  };

  const handleNoteSave = async (resourceId) => {
    const text = noteText[resourceId];
    if (text !== undefined) {
      await updateField(resourceId, 'notes', text);
      alert("Note saved!");
    }
  };

  // --- RENDER HELPERS ---
  const builtInFolders = ['Easy', 'Medium', 'Hard'];
  const metaFolders = resources.filter(r => r.type === 'folder_meta').map(r => r.folder);
  const resourceFolders = resources.filter(r => r.folder && r.type !== 'folder_meta').map(r => r.folder);
  const allFolders = [...new Set([...builtInFolders, ...metaFolders, ...resourceFolders])];

  const parents = resources.filter(r => !r.parentId && r.type !== 'folder_meta');
  const children = resources.filter(r => r.parentId);

  const renderUploadForm = (folder, parentId = null) => (
    <div style={styles.inlineForm}>
      <div style={styles.formTypeRow}>
        <label>
          <input
            type="radio"
            checked={addMode === 'file'}
            onChange={() => setAddMode('file')}
            style={{ marginRight: 4 }}
          />
          File
        </label>
        <label>
          <input
            type="radio"
            checked={addMode === 'link'}
            onChange={() => setAddMode('link')}
            style={{ marginRight: 4 }}
          />
          Link
        </label>
      </div>
      <div style={styles.formRow}>
        {addMode === 'file' ? (
          <input
            type="file"
            onChange={(e) => setFileObj(e.target.files[0])}
            style={{ fontSize: 13 }}
          />
        ) : (
          <>
            <input
              placeholder="Link Name"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
              style={{ ...styles.inputText, minWidth: '140px' }}
            />
            <input
              placeholder="URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              style={{ ...styles.inputText, flex: 1, minWidth: '160px' }}
            />
          </>
        )}
        <button
          onClick={() => handleUpload(folder, parentId)}
          disabled={uploading}
          style={styles.inlineSaveBtn}
        >
          {uploading ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={() => { setActiveUploadFolder(null); setActiveKeyParent(null); }}
          style={styles.inlineCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.titleBlock}>
          <h1 style={styles.title}>{subcategory}</h1>
          <p style={styles.subtitle}>{subject} &gt; {unit}</p>
          <div style={styles.metaRow}>
            {studentId && (
              <span style={styles.pill}>Student ID: {studentId}</span>
            )}
            <span style={styles.pill}>Teacher Material Manager</span>
          </div>
        </div>
        <button style={styles.backBtn} onClick={() => navigate('/')}>
          <span>←</span>
          <span>Back to Dashboard</span>
        </button>
      </div>

      {/* TOOLBAR: CREATE FOLDER */}
      <div style={styles.toolbarCard}>
        <div style={styles.toolbarLeft}>
          <div style={styles.toolbarTitle}>Organize resources into folders</div>
          <div style={styles.toolbarHint}>
            Create custom folders (e.g., “Homework Sets”, “Extra Practice”) to group files and links.
          </div>
        </div>
        <div style={styles.topBar}>
          <input 
            placeholder="Folder Name (e.g. Extra Practice)" 
            value={newFolderName} 
            onChange={(e) => setNewFolderName(e.target.value)} 
            style={styles.createFolderInput}
          />
          <button onClick={handleCreateFolder} style={styles.createFolderBtn}>
            + Add Folder
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loadingBox}>Loading resources…</div>
        ) : (
          <>
            {allFolders.map(folder => {
              const folderParents = parents.filter(r => (r.folder || 'General') === folder);
              const accent = getFolderAccent(folder);

              return (
                <div
                  key={folder}
                  style={{
                    ...styles.folderSection,
                    borderLeft: `4px solid ${accent}`,
                  }}
                >
                  {/* Folder Header */}
                  <div style={styles.folderHeaderRow}>
                    <div style={styles.folderTitleGroup}>
                      <h2 style={styles.folderTitle}>{folder}</h2>
                      <span style={styles.folderBadge}>
                        {folderParents.length} item{folderParents.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={styles.folderActions}>
                      <button
                        style={styles.folderActionBtn}
                        onClick={() => handleRenameFolder(folder)}
                      >
                        Rename
                      </button>
                      {!builtInFolders.includes(folder) && (
                        <button
                          style={styles.deleteFolderBtn}
                          onClick={() => handleDeleteFolder(folder)}
                        >
                          Delete
                        </button>
                      )}
                      <button 
                        style={styles.openUploadBtn} 
                        onClick={() => { setActiveUploadFolder(folder); setAddMode('file'); }}
                      >
                        + Add Resource
                      </button>
                    </div>
                  </div>

                  {/* Inline Upload Form for this folder */}
                  {activeUploadFolder === folder && renderUploadForm(folder)}

                  {/* Resources List */}
                  {folderParents.map(parent => {
                    const prog = progressMap[parent.id] || {};
                    const pct = getPct(prog.scoreCorrect, prog.scoreTotal);
                    const myChildren = children.filter(c => c.parentId === parent.id);
                    const currentNote = noteText[parent.id] !== undefined
                      ? noteText[parent.id]
                      : (prog.notes || '');

                    return (
                      <div key={parent.id} style={styles.resourceRow}>
                        {/* RESOURCE CARD */}
                        <div style={styles.resourceCard}>
                          {/* Header: name + status */}
                          <div style={styles.resourceHeader}>
                            <div
                              style={styles.resourceInfo}
                              onClick={() => window.open(parent.url, '_blank')}
                              title="Open resource in new tab"
                            >
                              <span style={styles.icon}>
                                {parent.type === 'file' ? '📄' : '🔗'}
                              </span>
                              <span style={styles.linkText}>{parent.name}</span>
                            </div>
                            <div style={styles.statusGroup}>
                              <button
                                style={styles.statusPill(
                                  prog.status === 'in-progress',
                                  '#eab308',
                                  '#fef9c3'
                                )}
                                onClick={() =>
                                  updateField(
                                    parent.id,
                                    'status',
                                    prog.status === 'in-progress' ? null : 'in-progress'
                                  )
                                }
                                title="Mark In-Progress"
                              >
                                <span>⏳</span>
                                <span>In Progress</span>
                              </button>
                              <button
                                style={styles.statusPill(
                                  prog.status === 'completed',
                                  '#22c55e',
                                  '#dcfce7'
                                )}
                                onClick={() =>
                                  updateField(
                                    parent.id,
                                    'status',
                                    prog.status === 'completed' ? null : 'completed'
                                  )
                                }
                                title="Mark Completed"
                              >
                                <span>✓</span>
                                <span>Completed</span>
                              </button>
                            </div>
                          </div>

                          {/* Body: score / notes / actions */}
                          <div style={styles.resourceBody}>
                            {/* Score / Progress */}
                            <div style={styles.scoreContainer}>
                              <span style={{ fontWeight: 600 }}>Score</span>
                              <div style={styles.scoreInputsRow}>
                                <input 
                                  style={styles.scoreInput} 
                                  placeholder="-"
                                  defaultValue={prog.scoreCorrect}
                                  onBlur={(e) => updateField(parent.id, 'scoreCorrect', e.target.value)}
                                />
                                <span>/</span>
                                <input 
                                  style={styles.scoreInput} 
                                  placeholder="-"
                                  defaultValue={prog.scoreTotal}
                                  onBlur={(e) => updateField(parent.id, 'scoreTotal', e.target.value)}
                                />
                              </div>
                              <div style={styles.battery()}>
                                <div style={styles.batteryFill(pct)} />
                              </div>
                              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                Mastery: {pct.toFixed(0)}%
                              </span>
                            </div>

                            {/* Notes */}
                            <div style={styles.noteContainer}>
                              <span style={styles.noteLabel}>Teacher Notes / Instructions</span>
                              <textarea 
                                style={styles.noteArea} 
                                placeholder="e.g. Use for homework after Lesson 3. Focus on questions 5–10."
                                value={currentNote}
                                onChange={(e) => setNoteText({ ...noteText, [parent.id]: e.target.value })}
                              />
                              <button
                                style={styles.saveNoteBtn}
                                onClick={() => handleNoteSave(parent.id)}
                              >
                                Save note
                              </button>
                            </div>

                            {/* Actions */}
                            <div style={styles.actionColumn}>
                              <button
                                style={styles.addKeyBtn}
                                onClick={() => { setActiveKeyParent(parent.id); setAddMode('file'); }}
                              >
                                + Add Answer Key
                              </button>
                              <button
                                style={styles.deleteBtn}
                                onClick={() => handleDeleteResource(parent)}
                              >
                                <span>🗑</span>
                                <span>Remove</span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Inline Upload for Answer Key */}
                        {activeKeyParent === parent.id && renderUploadForm(folder, parent.id)}

                        {/* CHILDREN (Answer Keys) */}
                        {myChildren.map(child => (
                          <div key={child.id} style={styles.rowChild}>
                            <div
                              style={styles.childInfo}
                              onClick={() => window.open(child.url, '_blank')}
                              title="Open answer key"
                            >
                              <span style={styles.childLabel}>Answer Key</span>
                              <span style={{ fontSize: 16 }}>📄</span>
                              <span style={styles.childName}>{child.name}</span>
                            </div>
                            <button
                              style={styles.deleteBtn}
                              onClick={() => handleDeleteResource(child)}
                            >
                              <span>🗑</span>
                              <span>Remove</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  {folderParents.length === 0 && (
                    <div style={styles.emptyFolder}>
                      No resources in this folder yet. Click “+ Add Resource” to upload a file or link.
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
